import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { SharedService } from 'src/app/core/services/shared.service';

interface Aviso {
  PAD29CON: string;
  PAD29AVI: 'N' | 'M' | 'C';
  PAD29TXT: string;
  VENCE: string;
  TSTAMP: string;
  clave: string;      // PAD29CON:TSTAMP -> si el aviso se edita, vuelve a salir
  venceMs: number;
  titulo: string;     
  texto: string;     
}

const URL_AVISOS = 'api/servicios/ConsultarAvisos';
const CONSULTA_MS = 10 * 60 * 1000;    
const MIN_ENTRE_CONSULTAS_MS = 60 * 1000;
const MAX_NOTICIAS = 10;
const SALIDA_MS = 320;                  

@Component({
  selector: 'app-avisos-sistema',
  templateUrl: './avisos-sistema.component.html',
  styleUrls: ['./avisos-sistema.component.scss']
})
export class AvisosSistemaComponent implements OnInit, OnDestroy {

  noticias: Aviso[] = [];
  mantenimiento: Aviso | null = null;
  critico: Aviso | null = null;
  cuenta: { d: string; h: string; m: string; s: string } | null = null;

  private actualClave: string | null = null;   
  private ultimoIndice = 0;

  private avisos: Aviso[] = [];
  private desfaseMs = 0;               
  private ultimaConsulta = 0;
  private consultando = false;
  private intervalo: any = null;
  private reloj: any = null;
  private alVolver = () => {
    if (document.visibilityState === 'visible' && Date.now() - this.ultimaConsulta > MIN_ENTRE_CONSULTAS_MS) {
      this.consultar();
    }
  };

  constructor(private dataService: SharedService, private ngZone: NgZone, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.limpiarCerradosViejos();
    this.consultar();
    this.ngZone.runOutsideAngular(() => {
      this.intervalo = setInterval(() => this.ngZone.run(() => this.consultar()), CONSULTA_MS);
    });
    document.addEventListener('visibilitychange', this.alVolver);
  }

  ngOnDestroy(): void {
    if (this.intervalo) clearInterval(this.intervalo);
    this.detenerReloj();
    document.removeEventListener('visibilitychange', this.alVolver);
  }

  // ---------------------------------------------------------------- consulta
  consultar(): void {
    if (this.consultando || !localStorage.getItem('JWT')) return;
    this.consultando = true;
    this.ultimaConsulta = Date.now();

    
    this.dataService.Consultar(URL_AVISOS, JSON.stringify({ spinner: 'N' })).subscribe({
      next: (data: any) => {
        this.consultando = false;
        const d = (data && data[0]) ? data[0] : {};
        const ahora = this.aFecha(d.AHORA);
        if (ahora) this.desfaseMs = ahora - Date.now();

        this.avisos = (d.AVISOS || []).map((a: any) => ({
          ...a,
          clave: `${a.PAD29CON}:${a.TSTAMP}`,
          venceMs: this.aFecha(a.VENCE) || 0,
          ...this.separar(a.PAD29TXT)
        }));

        this.limpiarLeidos();
        this.armar();
      },
      error: () => { this.consultando = false; }  
    });
  }

  // ---------------------------------------------------------------- acciones
  marcarLeido(a: Aviso): void {
    const leidos = this.leer(localStorage, this.llaveLeidos);
    if (!leidos.includes(a.clave)) leidos.push(a.clave);
    this.guardar(localStorage, this.llaveLeidos, leidos);
    this.ultimoIndice = this.indice;
    this.armar();
  }

  cerrar(a: Aviso): void {
    const cerrados = this.leer(sessionStorage, this.llaveCerrados);
    if (!cerrados.includes(a.clave)) cerrados.push(a.clave);
    this.guardar(sessionStorage, this.llaveCerrados, cerrados);
    this.ultimoIndice = this.indice;
    this.armar();
  }

  get indice(): number {
    const i = this.noticias.findIndex(n => n.clave === this.actualClave);
    return i >= 0 ? i : 0;
  }

  get noticia(): Aviso | null { return this.noticias[this.indice] || null; }

  anterior(): void { this.irA(this.indice - 1); }
  siguiente(): void { this.irA(this.indice + 1); }

  private irA(i: number): void {
    const n = this.noticias.length;
    if (!n) return;
    this.actualClave = this.noticias[(i + n) % n].clave;
  }

  trackAviso(_: number, a: Aviso): string { return a.clave; }

  saliendo = false;
  despedir(a: Aviso, accion: 'leer' | 'cerrar'): void {
    if (this.saliendo) return;
    this.saliendo = true;
    setTimeout(() => {
      this.saliendo = false;
      if (accion === 'leer') this.marcarLeido(a); else this.cerrar(a);
    }, SALIDA_MS);
  }

  get noticiaVisible(): Aviso[] { return this.noticia ? [this.noticia] : []; }

  // ---------------------------------------------------------------- armado
  private armar(): void {
    const leidos = this.leer(localStorage, this.llaveLeidos);
    const cerrados = this.leer(sessionStorage, this.llaveCerrados);
    const ahora = this.ahoraServidor();

    const vigentes = this.avisos.filter(a => a.venceMs > ahora);
    const pendientes = vigentes.filter(a => !leidos.includes(a.clave) && !cerrados.includes(a.clave));

    this.critico = pendientes.find(a => a.PAD29AVI === 'C') || null;
    this.mantenimiento = vigentes.find(a => a.PAD29AVI === 'M' && !cerrados.includes(a.clave)) || null;
    this.noticias = pendientes.filter(a => a.PAD29AVI === 'N').slice(0, MAX_NOTICIAS);

    if (this.noticias.length && !this.noticias.some(n => n.clave === this.actualClave)) {
      this.actualClave = this.noticias[Math.min(this.ultimoIndice, this.noticias.length - 1)].clave;
    }

    if (this.mantenimiento) this.iniciarReloj(); else this.detenerReloj();
  }

  private limpiarCerradosViejos(): void {
    try {
      const actual = this.llaveCerrados;
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('avisos_cerrados_') && k !== actual)
        .forEach(k => sessionStorage.removeItem(k));
    } catch { /* sin almacenamiento */ }
  }

  private limpiarLeidos(): void {
    const vigentes = new Set(this.avisos.map(a => a.clave));
    this.guardar(localStorage, this.llaveLeidos, this.leer(localStorage, this.llaveLeidos).filter(c => vigentes.has(c)));
  }

  // ---------------------------------------------------------------- cuenta regresiva
  private iniciarReloj(): void {
    this.tick();
    if (this.reloj) return;
    this.ngZone.runOutsideAngular(() => {
      this.reloj = setInterval(() => { this.tick(); this.cdr.detectChanges(); }, 1000);
    });
  }

  private detenerReloj(): void {
    if (this.reloj) { clearInterval(this.reloj); this.reloj = null; }
    this.cuenta = null;
  }

  private tick(): void {
    if (!this.mantenimiento) return;
    const ms = this.mantenimiento.venceMs - this.ahoraServidor();
    if (ms <= 0) { this.armar(); return; }
    const t = Math.floor(ms / 1000);
    const dos = (n: number) => String(n).padStart(2, '0');
    const d = Math.floor(t / 86400);
    this.cuenta = {
      d: d > 0 ? String(d) : '',
      h: dos(Math.floor((t % 86400) / 3600)),
      m: dos(Math.floor((t % 3600) / 60)),
      s: dos(t % 60)
    };
  }

  // ---------------------------------------------------------------- utilidades
  private get llaveLeidos(): string { return `avisos_leidos_${localStorage.getItem('S00USR') || ''}`; }
  private get llaveCerrados(): string {
    const jwt = localStorage.getItem('JWT') || '';
    return `avisos_cerrados_${localStorage.getItem('S00USR') || ''}_${jwt.slice(-16)}`;
  }

  private separar(txt: string): { titulo: string; texto: string } {
    const v = String(txt || '').trim();
    const i = v.indexOf('\n');
    return i > 0 ? { titulo: v.slice(0, i).trim(), texto: v.slice(i + 1).trim() } : { titulo: '', texto: v };
  }

  private ahoraServidor(): number { return Date.now() + this.desfaseMs; }

  private aFecha(v: string): number | null {
    if (!v) return null;
    const t = new Date(v).getTime();
    return isNaN(t) ? null : t;
  }

  private leer(storage: Storage, llave: string): string[] {
    try {
      const v = JSON.parse(storage.getItem(llave) || '[]');
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  }

  private guardar(storage: Storage, llave: string, valor: string[]): void {
    try { storage.setItem(llave, JSON.stringify(valor)); } catch { /* sin almacenamiento */ }
  }
}
